import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import useHealthCenterBrainFunctionMappingStyles from 'pages/HealthCenter/ReportV1/BrainFunctionMapping/Style'
import {getPublicFile} from 'helpers/envHelper'
import useLanguage from 'features/languageReport/useLanguage'

const getHealthReportReportImage = getPublicFile('images/health_center_report')

export type HeaderItem = {
  key: string
  content: string
  note: string
}

export type CellType =
  | {header: HeaderItem[]; image: string}
  | string[]
  | number
  | number[]
  | undefined

export interface DataTableProps {
  head: string[]
  body?:
    | {
        data: CellType[] | undefined
        background: string | undefined
      }[]
    | any
  isLocalLanguage?: boolean
}

const getNumber = (value: number | string) => {
  if (!Number.isNaN(Number(value))) return Number(value)
  return 0
}

const scoreToString = (score: number) => {
  if (Math.floor(score) < 30) {
    return 'IScoreBrainMapping_Lowering'
  }
  if (Math.floor(score) > 70) {
    return 'IScoreBrainMapping_Exaltation'
  }

  return 'IScoreBrainMapping_Balance'
}

const scoreToClass = (score: number) => {
  if (Math.floor(score) < 30) {
    return 'LoweringActive'
  }
  if (Math.floor(score) > 70) {
    return 'ExaltationActive'
  }

  return 'Balanced'
}

const scoreSign = (score: number | string) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score

  if (parsedScore < 30) {
    return getHealthReportReportImage('down.svg')
  }
  if (parsedScore > 70) {
    return getHealthReportReportImage('up.svg')
  }

  return getHealthReportReportImage('arrow.svg')
}

const Label = ({
  score,
  isLocalLanguage,
}: {
  score: number | string
  isLocalLanguage?: boolean
}) => {
  const {t} = useTranslation()
  const {textHealthAPI} = useLanguage('health')
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const string = scoreToString(getNumber(score))
  const className = scoreToClass(getNumber(score))

  const scoreToStringAPI = useCallback((score: number) => {
    if (Math.floor(score) < 30) {
      return textHealthAPI?.brain_mapping.status.low
    }
    if (Math.floor(score) > 70) {
      return textHealthAPI?.brain_mapping.status.high
    }

    return textHealthAPI?.brain_mapping.status.normal
  }, [])
  return (
    <div className={`${classes.labelScore} ${className}`}>
      {isLocalLanguage ? t(string) : scoreToStringAPI(getNumber(score))}
      <img src={scoreSign(score)} alt='icon' />
    </div>
  )
}

const Score = ({
  score,
  isLocalLanguage,
}: {
  score: number | string
  isLocalLanguage?: boolean
}) => {
  const {t, i18n} = useTranslation()
  const classes = useHealthCenterBrainFunctionMappingStyles()
  return (
    <div className={classes.bottom16}>
      <div className={classes.scoreNumber}>
        {score}
        {i18n.language !== 'en' && t('IScore')}
      </div>
      <Label score={score} isLocalLanguage={isLocalLanguage} />
    </div>
  )
}

const TableFiguresByPart = ({
  head,
  body,
  isLocalLanguage = true,
}: DataTableProps) => {
  const classes = useHealthCenterBrainFunctionMappingStyles()
  const DATA = {
    head,
    body,
  }

  const isObjectWithHeader = (
    cell: CellType,
  ): cell is {header: HeaderItem[]; image: string} => {
    return typeof cell === 'object' && cell !== null && 'header' in cell
  }

  return (
    <div className={classes.tableFiguresByPart}>
      <table>
        <thead>
          <tr>
            {DATA.head.map((item) => (
              <th>{item}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DATA.body.map(
            (
              row: {background: any; data: CellType[]},
              rowIndex: React.Key | null | undefined,
            ) => {
              return (
                <tr key={rowIndex} style={{background: `${row.background}`}}>
                  {row.data.map((cell: CellType, cellIndex) => {
                    if (Array.isArray(cell) && cell.length > 0) {
                      if (typeof cell[0] === 'string')
                        return (
                          <td key={cellIndex} className={classes.secondCol}>
                            <ul>
                              {cell.map((itemChild, childIndex) => (
                                <li key={`${cellIndex}-${childIndex}`}>
                                  {itemChild}
                                </li>
                              ))}
                            </ul>
                          </td>
                        )
                      return (
                        <td key={cellIndex} className={classes.scoreCol}>
                          <div className={classes.flexColScore}>
                            <div className={classes.widthMaxContent}>
                              {cell.map((itemChildScore) => (
                                <Score
                                  score={itemChildScore}
                                  isLocalLanguage={isLocalLanguage}
                                />
                              ))}
                            </div>
                          </div>
                        </td>
                      )
                    }
                    if (isObjectWithHeader(cell)) {
                      return (
                        <td key={cellIndex} className={classes.firstCol}>
                          <div className={classes.firstColFlex}>
                            <div className={classes.width100}>
                              {cell.header.map((headerItem) => (
                                <>
                                  <div className={classes.textRow1}>
                                    <div className={classes.keyRow1}>
                                      {headerItem.key}
                                    </div>
                                    &nbsp;
                                    <div className={classes.headerRow1}>
                                      <div
                                        className={classes.headerRow1Content}
                                      >
                                        {headerItem.content}
                                      </div>
                                      <div className={classes.headerRow1Note}>
                                        {headerItem.note}
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ))}
                              <div className={classes.imgBrain}>
                                {cell.image && (
                                  <img src={cell.image} alt='noImage' />
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      )
                    }
                    return (
                      <td key={cellIndex} className={classes.firstCol}>
                        <p>{cell}</p>
                      </td>
                    )
                  })}
                </tr>
              )
            },
          )}
        </tbody>
      </table>
    </div>
  )
}

export default TableFiguresByPart
